"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tenantSettingsUpdateInputSchema = exports.tenantSettingsSchema = exports.tenantRoleSchema = exports.roleNameSchema = exports.roleSlugSchema = exports.ROLE_NAME_MAX_LENGTH = exports.tenantThresholdsInputSchema = exports.tenantThresholdsSchema = exports.lphGroupsInputSchema = exports.lphGroupsSchema = exports.lphGroupSchema = exports.LPH_GROUPS_MAX = exports.LPH_GROUP_LABEL_MAX_LENGTH = exports.tenantModulesSchema = exports.CORE_MODULE = exports.tenantModuleSchema = exports.TENANT_MODULES = void 0;
exports.isTenantModule = isTenantModule;
exports.collectActiveModules = collectActiveModules;
exports.tenantModulesFrom = tenantModulesFrom;
exports.anzeigetextSchema = anzeigetextSchema;
exports.planTenantSettingsChange = planTenantSettingsChange;
exports.tenantSettingsChangeSchema = tenantSettingsChangeSchema;
const zod_1 = require("zod");
const common_js_1 = require("./common.js");
const rbac_js_1 = require("./rbac.js");
/**
 * Die Konfiguration eines Mandanten (AC-101).
 *
 * Der Schnitt entspricht dem aus AC-007: WAS es gibt, ist Code (die Module,
 * die Schwellwerte als Felder, die Leistungsphasen 1–9), WIE ein Mandant es
 * einstellt, sind Daten (eine Zeile in `tenant_settings`, die Rollennamen in
 * `roles.name`). Ein zweiter Mandant bekommt damit eigene Rollennamen, eine
 * eigene LPH-Gruppierung, eigene Schwellwerte und eine eigene Modulauswahl,
 * ohne dass jemand TypeScript anfasst oder neu ausrollt.
 *
 * Was hier BEWUSST fehlt: die Vorgabewerte. Sie stehen genau einmal, als
 * Spaltenvorgaben in der Migration `*_ac101_tenant_settings`. Die Zeile eines
 * neuen Mandanten entsteht dort per Trigger, und eine zweite Liste an dieser
 * Stelle waere die zweite Wahrheit, die beim naechsten Mandanten falsch ist —
 * dieselbe Linie wie bei den Standardrollen (CLAUDE.md §4, Regel 20).
 *
 * Transportfrei (ADR-04): nur Zod und reine Funktionen.
 */
// --- Module --------------------------------------------------------------------
/**
 * Die abschaltbaren Module. Wortgleich mit dem PostgreSQL-Enum `tenant_module`;
 * `tenant-module-catalog.e2e.spec.ts` vergleicht beide.
 *
 * ABSICHTLICH KURZ, dieselbe Regel wie beim Rechtekatalog: ein Modul kommt
 * dazu, wenn eine Prozedur es tatsaechlich traegt (`meta.module`) — nicht,
 * weil es plausibel klingt. Ein Schalter ohne Wirkung saehe in der
 * Konfiguration aus wie eine Zusage und waere keine.
 *
 * Die Reihenfolge ist die kanonische Speicherreihenfolge (dieselbe wie im
 * Enum). Ein neues Modul wird hinten angehaengt — nach CLAUDE.md §17.1 in
 * einer eigenen Migration mit `ALTER TYPE … ADD VALUE` und sonst nichts.
 */
exports.TENANT_MODULES = [
    /** PACS-Anbindung: Laufstatus und Schrittzuordnung (AC-504, AC-505). */
    'pacs',
    /** Ressourcenplanung und Freigabe (E4, heute der Pruefstein aus AC-007). */
    'ressourcenplanung',
];
exports.tenantModuleSchema = zod_1.z.enum(exports.TENANT_MODULES);
/**
 * „Kein abschaltbares Modul" — ausdruecklich erklaert, nicht durch Weglassen.
 *
 * Dieselbe Idee wie `ANY_AUTHENTICATED`: eine Prozedur OHNE Modulangabe ist
 * ein Versehen und laeuft nicht; eine Plattformfunktion, die jeder Mandant hat
 * (Audit, Konfiguration, Erreichbarkeit), sagt das mit diesem Wert. Er steht
 * nicht in `TENANT_MODULES` und laesst sich deshalb nicht abschalten.
 */
exports.CORE_MODULE = 'core';
const TENANT_MODULE_SET = new Set(exports.TENANT_MODULES);
/** Prueft einen Wert UNBEKANNTER Herkunft (Datenbank) gegen den Katalog. */
function isTenantModule(value) {
    return TENANT_MODULE_SET.has(value);
}
/**
 * Wertet die gespeicherten Modulwerte aus.
 *
 * Ein unbekannter Wert wird VERWORFEN und nicht geworfen — dieselbe
 * Entscheidung wie bei `collectPermissions` (AC-007) und aus demselben Grund:
 * die Migration laeuft VOR dem Deploy (CLAUDE.md §17.1). Fuehrt eine spaetere
 * Story ein Modul ein, kennt die noch laufende Version es im Rolloutfenster
 * nicht; ein Wurf legte dort jede modulgebundene Prozedur lahm. Verwerfen
 * heisst „wirkt hier nicht" — und die laufende Version hat fuer ein ihr
 * unbekanntes Modul ohnehin keine Prozedur.
 */
function collectActiveModules(values) {
    const bekannt = new Set();
    const unbekannt = [];
    for (const value of values) {
        if (isTenantModule(value)) {
            bekannt.add(value);
        }
        else if (!unbekannt.includes(value)) {
            unbekannt.push(value);
        }
    }
    return { modules: exports.TENANT_MODULES.filter((module) => bekannt.has(module)), unbekannt };
}
/** Jedes Modul mit seinem Zustand. Vollstaendig: ein fehlender Schluessel ist ein Fehler. */
exports.tenantModulesSchema = zod_1.z.record(exports.tenantModuleSchema, zod_1.z.boolean());
/** Die vollstaendige Schalterstellung aus der Menge der aktiven Module. */
function tenantModulesFrom(active) {
    const aktiv = new Set(active);
    return Object.fromEntries(exports.TENANT_MODULES.map((module) => [module, aktiv.has(module)]));
}
// --- Freitext ------------------------------------------------------------------
/**
 * Steuer- und Formatzeichen (Zeilenumbruch, Tabulator, Richtungswechsel,
 * Nullbreitenzeichen). In einem Rollennamen oder einer Gruppenbezeichnung hat
 * keines davon etwas verloren, und die Richtungswechsel sind ein bekannter Weg,
 * einen Namen in der Anzeige anders aussehen zu lassen, als er gespeichert ist.
 */
const STEUERZEICHEN = /[\p{Cc}\p{Cf}]/u;
function anzeigetextSchema(maxLength) {
    return zod_1.z
        .string()
        .trim()
        .min(1)
        .max(maxLength)
        .refine((value) => !STEUERZEICHEN.test(value), {
        message: 'Steuer- und Formatzeichen sind nicht zulässig',
    });
}
/** Vergleichsform fuer „gleicher Name": Unicode-normalisiert und ohne Gross/Klein. */
function vergleichsform(value) {
    return value.normalize('NFC').toLowerCase();
}
// --- Leistungsphasen-Gruppierung -------------------------------------------------
exports.LPH_GROUP_LABEL_MAX_LENGTH = 60;
/**
 * Hoechstens neun Gruppen: jede Leistungsphase liegt in hoechstens einer
 * Gruppe, und jede Gruppe hat mindestens eine.
 */
exports.LPH_GROUPS_MAX = 9;
const lphGroupFields = {
    label: anzeigetextSchema(exports.LPH_GROUP_LABEL_MAX_LENGTH),
    phases: zod_1.z.array(common_js_1.leistungsphaseSchema).min(1).max(exports.LPH_GROUPS_MAX),
};
/**
 * Die Regeln fuer eine Gruppierung, EINMAL — fuer Eingabe und gespeicherten
 * Stand gleichermassen.
 *
 * Geprueft wird, was eine Gruppierung widerspruechlich macht: eine Phase in
 * zwei Gruppen (oder zweimal in einer) und zwei Gruppen mit demselben Namen.
 *
 * BEWUSST NICHT geprueft, und das gehoert in den Review von AC-305:
 *
 *   - Vollstaendigkeit. Die mit SBI abgestimmte Startgruppierung
 *     (1+2, 3+4, 5, 6+7, 8) laesst LPH 9 aus. Eine Pflicht zur vollstaendigen
 *     Abdeckung wiese genau diese Vorgabe ab. Was mit einer ungruppierten
 *     Phase in einer Auswertung geschieht, entscheidet AC-305.
 *   - Zusammenhang. {1, 3} ist ungewoehnlich, aber keine Fehleingabe, die sich
 *     aus der HOAI begruenden liesse — das waere eine erfundene Geschaeftsregel.
 */
function pruefeLphGruppen(groups, ctx) {
    const gruppeJePhase = new Map();
    const gruppeJeName = new Map();
    groups.forEach((group, index) => {
        group.phases.forEach((phase, phaseIndex) => {
            const vorher = gruppeJePhase.get(phase);
            if (vorher === undefined) {
                gruppeJePhase.set(phase, index);
                return;
            }
            ctx.addIssue({
                code: 'custom',
                path: [index, 'phases', phaseIndex],
                message: vorher === index
                    ? `LPH ${String(phase)} steht in dieser Gruppe doppelt`
                    : `LPH ${String(phase)} steht bereits in Gruppe ${String(vorher + 1)}`,
            });
        });
        const name = vergleichsform(group.label);
        const gleichnamig = gruppeJeName.get(name);
        if (gleichnamig === undefined) {
            gruppeJeName.set(name, index);
        }
        else {
            ctx.addIssue({
                code: 'custom',
                path: [index, 'label'],
                message: `Der Name ist bereits an Gruppe ${String(gleichnamig + 1)} vergeben`,
            });
        }
    });
}
/**
 * Eine Gruppe, wie sie gelesen und ausgeliefert wird.
 *
 * `z.object` und nicht `z.strictObject`: fuegt eine spaetere Story ein Feld an
 * (etwa einen stabilen Schluessel fuer AC-705), muss die noch laufende Version
 * den gespeicherten Stand trotzdem lesen koennen (CLAUDE.md §17.1). Die
 * Eingabe bleibt streng — dort ist ein unbekanntes Feld ein Tippfehler.
 */
exports.lphGroupSchema = zod_1.z.object(lphGroupFields);
exports.lphGroupsSchema = zod_1.z
    .array(exports.lphGroupSchema)
    .min(1)
    .max(exports.LPH_GROUPS_MAX)
    .superRefine(pruefeLphGruppen);
exports.lphGroupsInputSchema = zod_1.z
    .array(zod_1.z.strictObject(lphGroupFields))
    .min(1)
    .max(exports.LPH_GROUPS_MAX)
    .superRefine(pruefeLphGruppen);
// --- Schwellwerte ----------------------------------------------------------------
/**
 * Die mandantenspezifischen Schwellwerte. Jeder Schwellwert ist ein eigenes
 * Feld mit eigener Bedeutung und eigenem Wertebereich — kein freier
 * Schluessel-Wert-Speicher, in dem ein Tippfehler als neuer Schwellwert
 * durchginge.
 *
 * Ein Schwellwert kommt dazu, wenn Code ihn LIEST — dieselbe Regel wie bei
 * Rechten und Modulen. Die Aggregationsschwelle aus ADR-11 („ab vier
 * Personen") steht deshalb noch nicht hier: sie gehoert mit der Auswertung,
 * die sie durchsetzt, zu AC-802.
 */
const thresholdFields = {
    /**
     * Warnschwelle des PACS-Abdeckungsgrads in Prozent (AC-505, AK-2). Liegt der
     * Anteil zugeordneter Arbeitspakete darunter, meldet die Abdeckung
     * `belowThreshold`. 0 heisst „nie warnen" — ausser, es gibt gar keine
     * Arbeitspakete: keine Zahl ist keine Entwarnung.
     */
    pacsMappingCoveragePercent: zod_1.z.number().int().min(0).max(100),
};
/** Wie gelesen und ausgeliefert. Tolerant gegen Felder einer neueren Version (§17.1). */
exports.tenantThresholdsSchema = zod_1.z.object(thresholdFields);
/**
 * Die Eingabe ist TEILWEISE: genannt wird nur, was sich aendern soll. Eine
 * Oberflaeche, die einen spaeter hinzugekommenen Schwellwert nicht kennt, setzt
 * ihn damit nicht versehentlich zurueck.
 */
exports.tenantThresholdsInputSchema = zod_1.z
    .strictObject(thresholdFields)
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Mindestens ein Schwellwert muss genannt sein',
});
// --- Rollen ------------------------------------------------------------------------
exports.ROLE_NAME_MAX_LENGTH = 80;
/**
 * Der Slug ist die STABILE Kennung einer Rolle. Er ist nicht umbenennbar: die
 * Rechtematrix der Standardrollen (`asas_default_role_permissions()`) ist ueber
 * ihn verknuepft, und spaetere Migrationen tragen neue Rechte ueber ihn nach.
 * Umbenennbar ist allein der Anzeigename.
 */
exports.roleSlugSchema = zod_1.z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'kein gültiger Rollen-Slug');
exports.roleNameSchema = anzeigetextSchema(exports.ROLE_NAME_MAX_LENGTH);
/** Eine Rolle des Mandanten, wie die Konfiguration sie zeigt — ohne ihre Rechte. */
exports.tenantRoleSchema = zod_1.z.strictObject({
    /** Fuer `audit.historie` (`entityType: 'roles'`): wer hat wann umbenannt. */
    id: common_js_1.uuidSchema,
    slug: zod_1.z.string(),
    name: zod_1.z.string(),
    baseType: rbac_js_1.roleBaseTypeSchema,
});
const roleRenameSchema = zod_1.z.strictObject({ slug: exports.roleSlugSchema, name: exports.roleNameSchema });
// --- Lesen -----------------------------------------------------------------------
/** Die Konfiguration eines Mandanten, wie `tenant.settings` sie ausliefert. */
exports.tenantSettingsSchema = zod_1.z.strictObject({
    /** Die Kennung der Konfigurationszeile — Schluessel fuer `audit.historie`. */
    id: common_js_1.uuidSchema,
    /**
     * Zaehlt jede gespeicherte Aenderung. Eine Aenderung nennt den Stand, den sie
     * gesehen hat (`expectedVersion`); weicht er ab, wird sie abgelehnt, statt
     * die Aenderung eines anderen still zu ueberschreiben (CLAUDE.md §20.3).
     */
    version: zod_1.z.number().int().nonnegative(),
    updatedAt: zod_1.z.iso.datetime(),
    roles: zod_1.z.array(exports.tenantRoleSchema),
    lphGroups: exports.lphGroupsSchema,
    thresholds: exports.tenantThresholdsSchema,
    modules: exports.tenantModulesSchema,
});
// --- Aendern ---------------------------------------------------------------------
/**
 * Eine Aenderung der Konfiguration. Jeder Bereich ist optional; genannt wird,
 * was sich aendern soll:
 *
 *   roleNames   nur die genannten Rollen werden umbenannt
 *   lphGroups   ersetzt die Gruppierung VOLLSTAENDIG — sie ist ein Ganzes
 *   thresholds  nur die genannten Schwellwerte
 *   modules     nur die genannten Module werden an- oder abgeschaltet
 *
 * Teilweise statt vollstaendig, wo es geht, aus demselben Grund wie bei den
 * Schwellwerten: eine aeltere Oberflaeche soll ein neueres Modul nicht dadurch
 * abschalten, dass sie es nicht kennt.
 */
exports.tenantSettingsUpdateInputSchema = zod_1.z
    .strictObject({
    expectedVersion: zod_1.z.number().int().nonnegative(),
    roleNames: zod_1.z
        .array(roleRenameSchema)
        .min(1)
        .max(100)
        .superRefine((renames, ctx) => {
        const gesehen = new Set();
        renames.forEach((rename, index) => {
            if (gesehen.has(rename.slug)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [index, 'slug'],
                    message: 'Die Rolle ist in dieser Änderung mehrfach genannt',
                });
            }
            gesehen.add(rename.slug);
        });
    })
        .optional(),
    lphGroups: exports.lphGroupsInputSchema.optional(),
    thresholds: exports.tenantThresholdsInputSchema.optional(),
    modules: zod_1.z
        .partialRecord(exports.tenantModuleSchema, zod_1.z.boolean())
        .refine((value) => Object.keys(value).length > 0, {
        message: 'Mindestens ein Modul muss genannt sein',
    })
        .optional(),
})
    .refine((input) => input.roleNames !== undefined ||
    input.lphGroups !== undefined ||
    input.thresholds !== undefined ||
    input.modules !== undefined, { message: 'Die Änderung nennt keinen Bereich' });
/** Reihenfolge zaehlt: sie ist die Anzeigereihenfolge der Gruppen und ihrer Phasen. */
function gleicheGruppierung(a, b) {
    return (a.length === b.length &&
        a.every((group, index) => {
            const andere = b[index];
            return (andere !== undefined &&
                group.label === andere.label &&
                group.phases.length === andere.phases.length &&
                group.phases.every((phase, phaseIndex) => phase === andere.phases[phaseIndex]));
        }));
}
/**
 * Vergleicht eine Aenderung mit dem gespeicherten Stand — rein und ohne
 * Datenbank, damit die Entscheidung ohne laufendes PostgreSQL pruefbar ist.
 *
 * Zwei Dinge stehen erst hier fest und nicht schon in der Eingabepruefung:
 *
 *   1. ob die genannte Rolle existiert. Eine Rolle eines FREMDEN Mandanten ist
 *      unter Row Level Security nicht sichtbar und sieht damit aus wie eine,
 *      die es nicht gibt — dieselbe Antwort, kein Orakel.
 *   2. ob ein Name danach doppelt vergeben ist. Zwei Rollen mit gleichem Namen
 *      waeren in jeder Auswahl ununterscheidbar, obwohl sie verschiedene Rechte
 *      tragen koennen.
 *
 * `changed: false` heisst: die Aenderung beschreibt, was schon gespeichert ist.
 * Das ist der Fall einer Wiederholung nach verlorener Antwort (§20.2 Punkt 12)
 * und wird deshalb auch bei ueberholter `expectedVersion` nicht als Konflikt
 * gewertet — es wuerde niemandes Aenderung ueberschrieben.
 */
function planTenantSettingsChange(current, input) {
    const issues = [];
    const renames = [];
    if (input.roleNames !== undefined) {
        const rolleJeSlug = new Map(current.roles.map((role) => [role.slug, role]));
        const nameJeSlug = new Map(current.roles.map((role) => [role.slug, role.name]));
        input.roleNames.forEach((rename, index) => {
            const rolle = rolleJeSlug.get(rename.slug);
            if (rolle === undefined) {
                issues.push({ path: ['roleNames', index, 'slug'], message: 'Unbekannte Rolle' });
                return;
            }
            nameJeSlug.set(rename.slug, rename.name);
            if (rolle.name !== rename.name) {
                renames.push({ id: rolle.id, name: rename.name });
            }
        });
        // Gemeldet wird an der GENANNTEN Rolle, denn dort muss die Eingabe
        // korrigiert werden — und nur dort: zwei gleichnamige Rollen, die diese
        // Aenderung gar nicht beruehrt, sind ein Altbestand und kein Grund, jede
        // andere Umbenennung abzuweisen.
        input.roleNames.forEach((rename, index) => {
            if (!rolleJeSlug.has(rename.slug))
                return;
            const form = vergleichsform(rename.name);
            const kollidiert = [...nameJeSlug].some(([slug, name]) => slug !== rename.slug && vergleichsform(name) === form);
            if (kollidiert) {
                issues.push({
                    path: ['roleNames', index, 'name'],
                    message: 'Der Name ist bereits an eine andere Rolle vergeben',
                });
            }
        });
    }
    const lphGroups = input.lphGroups !== undefined && !gleicheGruppierung(input.lphGroups, current.lphGroups)
        ? input.lphGroups
        : null;
    const thresholds = {};
    if (input.thresholds !== undefined) {
        for (const key of Object.keys(thresholdFields)) {
            const neu = input.thresholds[key];
            if (neu !== undefined && neu !== current.thresholds[key]) {
                thresholds[key] = neu;
            }
        }
    }
    const aktiv = new Set(current.activeModules);
    const activate = [];
    const deactivate = [];
    if (input.modules !== undefined) {
        for (const module of exports.TENANT_MODULES) {
            const soll = input.modules[module];
            if (soll === true && !aktiv.has(module))
                activate.push(module);
            if (soll === false && aktiv.has(module))
                deactivate.push(module);
        }
    }
    const changed = renames.length > 0 ||
        lphGroups !== null ||
        Object.keys(thresholds).length > 0 ||
        activate.length > 0 ||
        deactivate.length > 0;
    return { renames, lphGroups, thresholds, activate, deactivate, issues, changed };
}
/**
 * Die Eingabepruefung EINSCHLIESSLICH der Regeln gegen den gespeicherten
 * Stand, als ein Zod-Schema.
 *
 * Warum ein Schema und nicht nur `planTenantSettingsChange(...).issues`: ein
 * Befund soll in DERSELBEN Form ankommen wie jeder andere Eingabefehler —
 * Pfad und Meldung je Feld (`data.validation` der API). Das leistet nur ein
 * echter `ZodError` aus einer Pruefung. Ein von Hand gebauter ist in Zod 4
 * kein `Error` (gemessen gegen 4.5.4), tRPC wickelte ihn deshalb in einen
 * `UnknownCauseError` ein, und die Feldbefunde gingen unterwegs verloren.
 */
function tenantSettingsChangeSchema(current) {
    return exports.tenantSettingsUpdateInputSchema.superRefine((input, ctx) => {
        for (const issue of planTenantSettingsChange(current, input).issues) {
            ctx.addIssue({ code: 'custom', path: [...issue.path], message: issue.message });
        }
    });
}
//# sourceMappingURL=tenant-settings.js.map